import { useState } from 'react';
import { Button, Modal, Tabs } from '@indipia/ui-core';
import { deliveredNoticeText } from '../utils/custReqOrder.logic';
import { BULK_PREVIEW_MESSAGE as M } from '../utils/bulkPreview.logic';

/** 공종 하나의 미리보기 — 탭 1개. */
export interface BulkPreviewTab {
  key: string;
  label: string;
  /** 실제로 나가는 알림톡 본문 — null 이면 미리볼 내용이 없다 */
  body: string | null;
  /** body 가 null 일 때 보여 줄 안내 */
  emptyText: string;
  /** 이 공종과 함께 전달되는 고객 요청 건수 — 알림톡 본문에는 넣지 않고 미리보기 아래에 건수만 보인다 */
  customerRequestCount: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  status: 'loading' | 'error' | 'ready';
  tabs: BulkPreviewTab[];
  onRetry: () => void;
}

/**
 * 일괄 작업요청 [공종별 미리보기](941 S16-8) — 선택한 공종이 탭으로 나뉘고, 탭마다 실제로 나갈 알림톡 문구를 보여 준다.
 * 문구는 서버가 발송과 같은 규칙으로 만든 그대로이며, 고객 요청 목록은 본문에 넣지 않고 아래에 '고객 요청 n건 함께 전달'만 둔다.
 * 상위 트리에 data-group="02"(D-08 §6.4)가 있어야 --gt-* 토큰이 해석된다 — 이 모달 본문이 직접 둔다.
 */
export function BulkOrderPreviewModal({ open, onClose, status, tabs, onRetry }: Props) {
  const [activeKey, setActiveKey] = useState('');
  // 고른 탭이 목록에 없으면(처음 열 때 · 선택이 바뀐 뒤) 첫 탭
  const active = tabs.find((t) => t.key === activeKey) ?? tabs[0];
  const notice = active ? deliveredNoticeText(active.customerRequestCount) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={M.title}
      size="lg"
      footer={
        <Button variant="secondary" size="sm" onClick={onClose}>
          {M.close}
        </Button>
      }
    >
      <div data-group="02" className="flex w-full flex-col gap-03">
        {status === 'loading' && (
          <div className="rounded-lg border border-border bg-surface-raised px-06 py-10 text-center text-body-sm text-text-muted">
            {M.loading}
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center gap-03 rounded-lg border border-border bg-surface-raised px-06 py-10 text-center text-body-sm text-text-muted">
            {M.loadError}
            <Button variant="secondary" size="sm" onClick={onRetry}>
              {M.retry}
            </Button>
          </div>
        )}

        {status === 'ready' && active && (
          <>
            <p className="text-caption text-text-muted">{M.hint}</p>
            {/* 탭이 많으면 한 줄을 유지하고 가로로 넘긴다 */}
            <Tabs
              variant="line"
              className="min-w-0 overflow-x-auto whitespace-nowrap"
              value={active.key}
              onChange={setActiveKey}
              tabs={tabs.map((t) => ({ label: t.label, value: t.key }))}
            />
            {active.body != null ? (
              <div className="mx-auto w-full max-w-[420px]">
                <div
                  className="max-h-[50vh] overflow-y-auto rounded-lg border px-04 py-04"
                  style={{ background: 'var(--gt-subtle)', borderColor: 'var(--gt-subtle)' }}
                >
                  <p className="whitespace-pre-wrap break-words text-body-sm leading-relaxed text-text">{active.body}</p>
                </div>
                {notice && <p className="mt-03 text-caption font-medium text-text-secondary">{notice}</p>}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border px-06 py-10 text-center text-body-sm text-text-muted">
                {active.emptyText}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
